/**
 * Kaçış level generator: Level N = generateLevel(N). Pure, seeded, cached, solvable by construction.
 *
 * Reverse construction. Escape order = target word order (worm k carries letter k and leaves k-th).
 * Worms are placed last-escaper first: worm k gets a gate, a self-avoiding "track" from the gate inward,
 * and sits on the far end of it, so it can always slide out along the track provided worms 0..k-1 are
 * gone (walls, live numbered stones and worms k+1.. were avoided by its track). Later-placed (earlier-
 * escaping) worms are biased to sit ON the tracks of earlier-placed ones → mutual blocking / ordering
 * puzzles. Numbered stones are put only on tracks of worms that leave late enough for them to have
 * dissolved. A few reversible "scramble" gestures then drag worms off their start spots; undoing them
 * (gestures are exactly invertible) is the first part of the witness solution, so levels also need the
 * occasional sidestep. The witness is replayed by the real engine; the bot compresses it and grades the
 * level with noisy runs; the move budget is fitted to the band the level number asks for.
 *
 * From level 21 on the chapter designer (@enjoyment/core designer.ts) picks each level's difficulty (one of the
 * levels 15–42 of the ramp below: warm-ups, build-ups, hard levels with breathers, a finale), the bot's win-rate
 * floor and the chapter's twist; the word, seeds and hint still follow the real level number, and the bot still
 * validates every level.
 */
import { Rng, designLevel, hashSeed, type DesignSpec, type DesignedLevel } from "@enjoyment/core";
import { applyGesture, beginDrag, cloneState, createGame, dragStep, endDrag, invertGesture, probe, replay, wordStar, type GameState } from "./game";
import { bandOf, compressWitness, GRADE_RUNS, noisyMoves, openings, solve, solveGreedy } from "./bot";
import { DX, DY, DIRS, opposite, type Cell, type Dir, type End, type Gate, type Gesture, type KacisLevel, type StoneDef, type WormDef } from "./types";
import { letters } from "./words";
import { lexicon, type WordLang } from "../../../shared/words";

export interface Spec {
  cols: number;
  rows: number;
  worms: number;
  minLen: number;
  maxLen: number;
  minSteps: number;
  maxSteps: number;
  walls: number;
  stones: number;
  scrambles: number;
  blockBias: number;
  /** Required win-rate floor for the fitted budget (band target). */
  floor: number;
  /** Minimum extra moves over the bot's clean par. */
  minSlack: number;
  /** Max worms that may leave at the very start (keeps ordering puzzles honest). */
  maxOpen: number;
  /** Require a level that cannot be finished by escapes alone (some worm must sidestep first). */
  needSidestep: boolean;
}

/**
 * Chapter design (levels 21+). Twists are variations the ramp already has: a chapter of numbered stones, of
 * tangled starts (more scramble gestures, so more sidesteps), of long worms (4–5 cells), or of walled boards.
 */
export const DESIGN: DesignSpec = { game: "kacis", floor: 15, ceiling: 42, twists: ["stones", "tangle", "long", "maze"] };

/** Board size of level n (designed levels: the size of their ramp level). */
export function sizeFor(n: number): { cols: number; rows: number } {
  const d = designLevel(DESIGN, n);
  return rampSize(d ? d.effective : n);
}

/** Worms (= letters of the hidden word) of level n. */
export function wormCountFor(n: number): number {
  const d = designLevel(DESIGN, n);
  return d ? designedWorms(d) : rampWormCount(n);
}

export function specFor(n: number): Spec {
  const d = designLevel(DESIGN, n);
  return d ? designedSpec(d) : rampSpec(n);
}

function rampSize(n: number): { cols: number; rows: number } {
  if (n <= 3) return { cols: 6, rows: 6 };
  if (n <= 7) return { cols: 6, rows: 7 };
  if (n <= 11) return { cols: 7, rows: 7 };
  if (n <= 15) return { cols: 7, rows: 8 };
  if (n <= 20) return { cols: 8, rows: 8 };
  if (n <= 26) return { cols: 8, rows: 9 };
  if (n <= 33) return { cols: 9, rows: 9 };
  return { cols: 9, rows: 10 };
}

/** The ramp's worm count before its rest rhythm: 3 → 6 worms over the first 13 levels. */
const rampWorms = (n: number): number => Math.max(3, Math.min(6, 3 + (n >= 3 ? 1 : 0) + (n >= 7 ? 1 : 0) + (n >= 13 ? 1 : 0)));

/** Hand-tuned levels: every fourth level from 8 on rests with one worm fewer. */
function rampWormCount(n: number): number {
  return Math.max(3, rampWorms(n) - (n >= 8 && n % 4 === 0 ? 1 : 0));
}

/** Designed levels: the ramp's worm count at their difficulty; the chapter's breathers rest with one worm fewer. */
function designedWorms(d: DesignedLevel): number {
  return Math.max(3, rampWorms(d.effective) - (d.role === "breather" ? 1 : 0));
}

/** The hand-tuned ramp (levels 1–20, and the difficulty scale the designer draws from). */
export function rampSpec(n: number): Spec {
  const { cols, rows } = rampSize(n);
  const worms = rampWormCount(n);
  const expert = n >= 20 && n % 10 === 0;
  return {
    cols,
    rows,
    worms,
    minLen: n < 10 ? 2 : 3,
    maxLen: n < 3 ? 3 : n < 8 ? 4 : 5,
    minSteps: n < 4 ? 1 : 2,
    maxSteps: Math.min(6, 2 + Math.floor(n / 6)),
    walls: n < 4 ? 0 : Math.min(24, Math.round(1 + (n - 4) * 0.6)),
    stones: n < 6 ? 0 : n < 14 ? 1 : n < 24 ? 2 : 3,
    scrambles: n < 5 ? 0 : Math.min(5, 1 + Math.floor((n - 5) / 6)),
    blockBias: Math.min(0.95, 0.2 + n * 0.03),
    floor: n <= 3 ? 0.97 : n < 13 ? 0.85 : n < 25 ? 0.65 : expert ? 0.35 : 0.55,
    minSlack: n <= 3 ? 3 : n < 25 ? 2 : 1,
    maxOpen: n < 3 ? worms : n < 8 ? worms - 1 : Math.max(1, Math.ceil(worms / 2)),
    needSidestep: (n >= 6 && n % 2 === 0) || n >= 15,
  };
}

/**
 * Difficulty of the ramp's level `d.effective`, with the designer's rhythm instead of the ramp's own (rest worm on
 * breathers instead of every fourth level, the slot's win-rate floor instead of the every-tenth expert level), and
 * the chapter's twist on top.
 */
function designedSpec(d: DesignedLevel): Spec {
  const base = rampSpec(d.effective);
  const worms = designedWorms(d);
  const spec: Spec = {
    ...base,
    worms,
    maxOpen: Math.max(1, Math.ceil(worms / 2)),
    floor: d.winRate.target,
  };
  switch (d.twist) {
    case "stones":
      return { ...spec, stones: spec.stones + 2 };
    case "tangle":
      return { ...spec, scrambles: spec.scrambles + 2 };
    case "long":
      return { ...spec, minLen: 4 };
    case "maze":
      return { ...spec, walls: spec.walls + 6 };
    default:
      return spec;
  }
}

// ---------------------------------------------------------------- word choice

const wordPerm = new Map<string, string[]>();
/** First word of each language: a no-friction introduction, a familiar three-letter word. */
const STARTER: Record<WordLang, string> = { tr: "KEK", en: "CAT" };
function permFor(len: number, lang: WordLang): string[] {
  const key = `${lang}:${len}`;
  let p = wordPerm.get(key);
  if (!p) {
    const list = lexicon(lang).words.filter((w) => letters(w).length === len);
    // Turkish keeps its original seed, so every Turkish level stays exactly as before English existed.
    const rng = new Rng(hashSeed(lang === "tr" ? `kacis-words-${len}` : `kacis-words-${lang}-${len}`));
    p = list.slice();
    for (let i = p.length - 1; i > 0; i--) {
      const j = rng.int(i + 1);
      [p[i], p[j]] = [p[j]!, p[i]!];
    }
    if (len === 3) {
      const starter = p.indexOf(STARTER[lang]);
      if (starter > 0) [p[0], p[starter]] = [p[starter]!, p[0]!];
    }
    wordPerm.set(key, p);
  }
  return p;
}

/** Target word of level n: a seeded shuffle of the list, consumed in level order (no repeats until exhausted). */
export function wordFor(n: number, lang: WordLang = "tr"): string {
  const len = wormCountFor(n);
  let used = 0;
  for (let m = 1; m < n; m++) if (wormCountFor(m) === len) used++;
  const p = permFor(len, lang);
  return p[used % p.length]!;
}

export function scrambleWord(word: string, seed: number): string {
  const ls = letters(word);
  const rng = new Rng(seed);
  for (let t = 0; t < 30; t++) {
    const a = ls.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = rng.int(i + 1);
      [a[i], a[j]] = [a[j]!, a[i]!];
    }
    if (a.join("") !== word) return a.join("");
  }
  return ls.slice().reverse().join("");
}

// ---------------------------------------------------------------- construction helpers

function shuffle<T>(a: T[], rng: Rng): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = rng.int(i + 1);
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Randomised self-avoiding walk of `len` cells starting at `start`, avoiding `blocked`. */
function walk(start: number, len: number, blocked: Uint8Array, cols: number, rows: number, rng: Rng, turnBias: number): number[] | null {
  const path = [start];
  const inPath = new Set<number>([start]);
  const dirsAt: Dir[][] = [];
  let budget = 600;
  const orderFor = (prev: number, cur: number): Dir[] => {
    const ds = shuffle(DIRS.slice() as Dir[], rng);
    if (prev >= 0 && rng.next() < turnBias) {
      const pd = (cur % cols) - (prev % cols) !== 0 ? ((cur % cols) - (prev % cols) > 0 ? 1 : 3) : cur - prev > 0 ? 2 : 0;
      ds.sort((a, b) => (a === pd ? 1 : 0) - (b === pd ? 1 : 0));
    }
    return ds;
  };
  dirsAt.push(orderFor(-1, start));
  while (path.length < len) {
    if (budget-- <= 0) return null;
    const cur = path[path.length - 1]!;
    const opts = dirsAt[dirsAt.length - 1]!;
    let advanced = false;
    while (opts.length) {
      const d = opts.pop()!;
      const x = (cur % cols) + DX[d];
      const y = Math.floor(cur / cols) + DY[d];
      if (x < 0 || y < 0 || x >= cols || y >= rows) continue;
      const id = y * cols + x;
      if (blocked[id] || inPath.has(id)) continue;
      path.push(id);
      inPath.add(id);
      dirsAt.push(orderFor(cur, id));
      advanced = true;
      break;
    }
    if (!advanced) {
      if (path.length === 1) return null;
      inPath.delete(path.pop()!);
      dirsAt.pop();
    }
  }
  return path;
}

const dirBetween = (cols: number, a: number, b: number): Dir => {
  const dx = (b % cols) - (a % cols);
  if (dx === 1) return 1;
  if (dx === -1) return 3;
  return b > a ? 2 : 0;
};

interface Built {
  worms: WormDef[];
  gates: Gate[];
  stones: StoneDef[];
  /** Raw witness: scramble undo gestures, then each worm's slide out. */
  raw: Gesture[];
}

function buildAttempt(spec: Spec, rng: Rng, word: string): Built | null {
  const { cols, rows } = spec;
  const ws = letters(word);
  const nW = ws.length;
  const palette = shuffle([0, 1, 2, 3, 4, 5, 6, 7], rng).slice(0, nW);
  const blocked = new Uint8Array(cols * rows); // start cells of already-placed worms
  const travelUnion = new Uint8Array(cols * rows); // cells later escapers will cross
  const wormsOut: WormDef[] = new Array(nW);
  const gates: Gate[] = [];
  const baseWitness: Gesture[] = new Array(nW);
  const usedGate = new Set<number>();
  const allTravel: number[][] = new Array(nW);
  const allStart: number[][] = new Array(nW);

  const borderSlots: { x: number; y: number; dir: Dir }[] = [];
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < cols; x++)
      for (const d of DIRS) {
        const ox = x + DX[d];
        const oy = y + DY[d];
        if (ox < 0 || oy < 0 || ox >= cols || oy >= rows) borderSlots.push({ x, y, dir: d });
      }

  for (let k = nW - 1; k >= 0; k--) {
    let best: { score: number; path: number[]; s: number; L: number; slot: { x: number; y: number; dir: Dir } } | null = null;
    for (let t = 0; t < 16; t++) {
      const slot = borderSlots[rng.int(borderSlots.length)]!;
      const g0 = slot.y * cols + slot.x;
      if (blocked[g0] || usedGate.has(g0 * 4 + slot.dir)) continue;
      const L = spec.minLen + rng.int(spec.maxLen - spec.minLen + 1);
      const s = spec.minSteps + rng.int(spec.maxSteps - spec.minSteps + 1);
      const path = walk(g0, s + L, blocked, cols, rows, rng, 0.55);
      if (!path) continue;
      let overlap = 0;
      for (let i = s; i < s + L; i++) if (travelUnion[path[i]!]) overlap++;
      let turns = 0;
      for (let i = 2; i < path.length; i++) if (dirBetween(cols, path[i - 2]!, path[i - 1]!) !== dirBetween(cols, path[i - 1]!, path[i]!)) turns++;
      const score = spec.blockBias * overlap * 3 + turns * 0.35 + rng.next();
      if (!best || score > best.score) best = { score, path, s, L, slot };
    }
    if (!best) return null;
    const { path, s, L, slot } = best;
    const starts = path.slice(s, s + L);
    const travel = path.slice(0, s);
    for (const id of starts) blocked[id] = 1;
    for (const id of travel) travelUnion[id] = 1;
    usedGate.add(path[0]! * 4 + slot.dir);
    gates.push({ x: slot.x, y: slot.y, dir: slot.dir, color: palette[k]! });
    const leadHead = rng.int(2) === 0;
    const cellsIds = leadHead ? starts : starts.slice().reverse();
    const cells: Cell[] = cellsIds.map((id) => ({ x: id % cols, y: Math.floor(id / cols) }));
    wormsOut[k] = { id: k, color: palette[k]!, letter: ws[k]!, cells };
    const steps: Dir[] = [];
    for (let i = s; i > 0; i--) steps.push(dirBetween(cols, path[i]!, path[i - 1]!));
    steps.push(slot.dir);
    baseWitness[k] = { worm: k, end: leadHead ? "head" : "tail", steps };
    allTravel[k] = travel;
    allStart[k] = starts;
  }

  // numbered stones on tracks of late escapers only
  const taken = new Uint8Array(cols * rows);
  for (let k = 0; k < nW; k++) for (const id of allStart[k]!) taken[id] = 1;
  const stones: StoneDef[] = [];
  const users = (id: number): number[] => {
    const u: number[] = [];
    for (let k = 0; k < nW; k++) if (allTravel[k]!.includes(id)) u.push(k);
    return u;
  };
  const trackCells: number[] = [];
  for (let k = 1; k < nW; k++) for (const id of allTravel[k]!) if (!taken[id] && !trackCells.includes(id)) trackCells.push(id);
  shuffle(trackCells, rng);
  for (const id of trackCells) {
    if (stones.length >= spec.stones) break;
    const u = users(id);
    const minK = Math.min(...u);
    if (minK < 1) continue;
    const nStone = rng.next() < 0.6 ? minK : 1 + rng.int(minK);
    stones.push({ x: id % cols, y: Math.floor(id / cols), n: nStone });
    taken[id] = 1;
  }
  // walls on cells no track needs
  const needed = new Uint8Array(cols * rows);
  for (let k = 0; k < nW; k++) {
    for (const id of allTravel[k]!) needed[id] = 1;
    for (const id of allStart[k]!) needed[id] = 1;
  }
  for (const st of stones) needed[st.y * cols + st.x] = 1;
  const spare: number[] = [];
  for (let id = 0; id < cols * rows; id++) if (!needed[id]) spare.push(id);
  shuffle(spare, rng);
  // keep roughly a third of the leftover cells open so worms can sidestep
  const maxWalls = Math.max(0, Math.min(spec.walls, Math.floor(spare.length * 0.66)));
  for (let i = 0; i < maxWalls; i++) {
    const id = spare[i]!;
    stones.push({ x: id % cols, y: Math.floor(id / cols), n: 0 });
  }

  // reversible scramble: drag worms off their start spots; the inverse gestures open the witness
  const partial: KacisLevel = {
    n: 0, seed: 0, cols, rows, worms: wormsOut, gates, stones, word, hint: word, budget: 1e9, par: 0, starPar: 0, band: "easy", winRate: 1, solution: [],
  };
  const st = createGame(partial, 1e9);
  const undo: Gesture[] = [];
  for (let i = 0; i < spec.scrambles; i++) {
    for (let tries = 0; tries < 14; tries++) {
      const wi = rng.int(nW);
      const end: End = rng.int(2) === 0 ? "head" : "tail";
      const before = cloneState(st);
      if (!beginDrag(st, wi, end)) continue;
      const want = 1 + rng.int(4);
      for (let q = 0; q < want; q++) {
        const opts = DIRS.filter((d) => {
          const p = probe(st, wi, end, d);
          return p.ok && p.kind === "move";
        });
        if (!opts.length) break;
        dragStep(st, opts[rng.int(opts.length)]!);
      }
      const steps = st.gesture ? st.gesture.steps.slice() : [];
      endDrag(st);
      const g: Gesture = { worm: wi, end, steps };
      const inv = steps.length ? invertGesture(before, g) : null;
      let ok = false;
      if (inv) {
        const probeState = cloneState(st);
        applyGesture(probeState, inv);
        ok = probeState.worms[wi]!.cells.every((c, idx) => c.x === before.worms[wi]!.cells[idx]!.x && c.y === before.worms[wi]!.cells[idx]!.y);
      }
      if (ok) {
        undo.push(inv!);
        break;
      }
      // roll back
      Object.assign(st, before);
    }
  }
  for (const w of wormsOut) w.cells = st.worms[w.id]!.cells.map((c) => ({ x: c.x, y: c.y }));
  const raw = undo.reverse().concat(baseWitness);
  return { worms: wormsOut, gates, stones, raw };
}

// ---------------------------------------------------------------- level assembly

function fitBudget(level: KacisLevel, spec: Spec): number {
  const sv = solve(level);
  const base = Math.max(sv.moves, sv.starMoves);
  const moves = noisyMoves(level, GRADE_RUNS);
  let b = base + spec.minSlack;
  for (; b < base + 40; b++) {
    const rate = moves.filter((m) => m <= b).length / moves.length;
    if (rate >= spec.floor) break;
  }
  return b;
}

export function generateLevelUncached(n: number): KacisLevel {
  const spec = specFor(n);
  const word = wordFor(n);
  const seed = hashSeed(`kacis:${n}`);
  let fallback: KacisLevel | null = null;
  for (let attempt = 0; attempt < 90; attempt++) {
    const rng = new Rng(hashSeed(`kacis:${n}:${attempt}`));
    const built = buildAttempt(spec, rng, word);
    if (!built) continue;
    const level: KacisLevel = {
      n,
      seed,
      cols: spec.cols,
      rows: spec.rows,
      worms: built.worms,
      gates: built.gates,
      stones: built.stones,
      word,
      hint: scrambleWord(word, hashSeed(`kacis-hint:${n}`)),
      budget: 1e9,
      par: 0,
      starPar: 0,
      band: "easy",
      winRate: 1,
      solution: [],
    };
    // the raw witness must win the real engine in word order
    const check = replay(level, built.raw, 1e9);
    if (!(check.status === "won" && wordStar(check))) continue;
    const compact = compressWitness(level, built.raw);
    if (!compact) continue;
    level.solution = compact;
    const open = openings(createGame(level, 1e9));
    const sv = solve(level);
    if (!sv.solved) continue;
    const quality = open <= spec.maxOpen && (spec.maxOpen >= spec.worms || open < spec.worms) && (!spec.needSidestep || solveGreedy(level) === null);
    if (!quality && attempt < 70) {
      fallback ??= level;
      continue;
    }
    level.par = sv.moves;
    level.starPar = sv.starMoves;
    level.budget = fitBudget(level, spec);
    const moves = noisyMoves(level, GRADE_RUNS);
    level.winRate = moves.filter((m) => m <= level.budget).length / moves.length;
    level.band = bandOf(level.winRate);
    if (level.band === "broken") continue;
    return level;
  }
  if (fallback) {
    const sv = solve(fallback);
    fallback.par = sv.moves;
    fallback.starPar = sv.starMoves;
    fallback.budget = fitBudget(fallback, spec);
    const moves = noisyMoves(fallback, GRADE_RUNS);
    fallback.winRate = moves.filter((m) => m <= fallback.budget).length / moves.length;
    fallback.band = bandOf(fallback.winRate);
    return fallback;
  }
  throw new Error(`kacis: could not generate level ${n}`);
}

const cache = new Map<string, KacisLevel>();
/**
 * Level N, N >= 1. Same N and language → same level, everywhere (client, bot, video factory).
 * Another language reuses the Turkish layout and only relabels the worms with a word of the same length:
 * letters are labels to the rules and the bot, so par, budget and band are identical in every language.
 */
export function generateLevel(n: number, lang: WordLang = "tr"): KacisLevel {
  const k = Math.max(1, Math.floor(n));
  const key = `${lang}:${k}`;
  let lv = cache.get(key);
  if (!lv) {
    if (lang === "tr") lv = generateLevelUncached(k);
    else {
      const base = generateLevel(k, "tr");
      const word = wordFor(k, lang);
      const ws = letters(word);
      lv = {
        ...base,
        worms: base.worms.map((w) => ({ ...w, letter: ws[w.id]! })),
        word,
        hint: scrambleWord(word, hashSeed(`kacis-hint:${lang}:${k}`)),
      };
    }
    cache.set(key, lv);
  }
  return lv;
}

export { opposite };
